<script setup lang="ts">
import { computed, ref } from 'vue';
import type { AuthResetPasswordApproveCodeRouteRpc, AuthResetPasswordDoResetRouteRpc, AuthResetPasswordSendCodeRouteRpc } from '@repo/contract';
import { useNotification } from '../../shared/composables/notification/use-notification';
import ResetPasswordFirstStep from './steps/reset-password-first-step.vue';
import ResetPasswordSecondStep from './steps/reset-password-second-step.vue';
import type {
  ResetPasswordFirstStepData,
  ResetPasswordSecondStepData,
  ResetPasswordStep,
  ResetPasswordStepData,
  ResetPasswordThirdStepData,
} from './types/steps';
import ResetPasswordThirdStep from './steps/reset-password-third-step.vue';
import { useResetPasswordValidation } from './validation/use-reset-password-validation';
import { useHead, useRouter } from '#app';
import { getRewardfulScripts } from '~/utils/rewardful-scripts';
import BaseButton from '~/shared/components/buttons/base-button.vue';
import BaseClickableText from '~/shared/components/buttons/base-clickable-text.vue';
import LogoBig from '~/shared/components/logo/logo-big.vue';
import { fetchRpc } from '~/shared/transport/fetch-rpc';

useHead({
  title: 'Reset Password | WeEngrave',
  script: [
    ...getRewardfulScripts(),
  ],
});

const step = ref<ResetPasswordStep>('first');

const componentStrategies = {
  first: ResetPasswordFirstStep,
  second: ResetPasswordSecondStep,
  third: ResetPasswordThirdStep,
};

const stepStrategies = ref({
  first: {
    showOneButton: true,
    buttonLabel: 'Send a code',
    data: {
      email: '',
    },
  },
  second: {
    showOneButton: false,
    buttonLabel: 'Confirm',
    data: {
      verificationCode: '',
    },
  },
  third: {
    showOneButton: true,
    buttonLabel: 'Submit',
    data: {
      password: '',
      passwordConfirmation: '',
    },
  },
});

const {
  codeErrors,
  emailErrors,
  passwordErrors,
  trigger,
} = useResetPasswordValidation(step, {
  emailData: computed(() => stepStrategies.value.first.data as ResetPasswordFirstStepData),
  codeData: computed(() => stepStrategies.value.second.data as ResetPasswordSecondStepData),
  passwordData: computed(() => stepStrategies.value.third.data as ResetPasswordThirdStepData),
});

async function sendCode() {
  await fetchRpc<typeof AuthResetPasswordSendCodeRouteRpc>(
    '/api/rpc/auth/reset-password/send-code',
    stepStrategies.value.first.data,
  );
}

async function approveCode() {
  await fetchRpc<typeof AuthResetPasswordApproveCodeRouteRpc>(
    '/api/rpc/auth/reset-password/approve-code',
    {
      code: stepStrategies.value.second.data.verificationCode,
      email: stepStrategies.value.first.data.email,
    },
  );
}

async function doReset() {
  await fetchRpc<typeof AuthResetPasswordDoResetRouteRpc>(
    '/api/rpc/auth/reset-password/do-reset',
    {
      code: stepStrategies.value.second.data.verificationCode,
      email: stepStrategies.value.first.data.email,
      password: stepStrategies.value.third.data.password,
    },
  );
}

const router = useRouter();
const { showNotification } = useNotification();

const codeSentAt = ref<number | null>(null);

async function goToNextStep() {
  if (!await trigger()) {
    setTimeout(() => {
      console.log('ERRORS: ', codeErrors.value, emailErrors.value, passwordErrors.value);
    }, 50);
    return;
  }

  if (step.value === 'first') {
    await sendCode();

    codeSentAt.value = Date.now();

    step.value = 'second';

    return;
  }

  if (step.value === 'second') {
    await approveCode();

    step.value = 'third';

    return;
  }

  if (step.value === 'third') {
    await doReset();

    showNotification({
      type: 'success',
      message: 'Password reset successfully',
    });

    router.push('/login');
  }
}

async function resendCode() {
  await sendCode();

  codeSentAt.value = Date.now();

  showNotification({
    type: 'success',
    message: 'Code sent successfully',
  });
}

function goToPreviousStep() {
  if (step.value === 'second') {
    step.value = 'first';
  }
}

const currentStepData = computed(() => {
  return stepStrategies.value[step.value].data as ResetPasswordStepData;
});

const showOneButton = computed(() => {
  console.log('Show one button: ', stepStrategies.value[step.value].showOneButton, step.value);
  return stepStrategies.value[step.value].showOneButton;
});

const buttonLabel = computed(() => {
  return stepStrategies.value[step.value].buttonLabel;
});
</script>

<template>
  <div class="container mx-auto h-100dvh flex flex-col justify-center items-center py-28">
    <form class="flex flex-col w-full h-full items-center justify-between max-h-175" @submit.prevent>
      <div class="flex flex-col items-center gap-8">
        <h1 class="text-h3 text-center w-full">
          Password Reset
        </h1>

        <LogoBig class="h-7.5" />
      </div>

      <Transition
        enter-from-class="opacity-0" leave-to-class="opacity-0"
        mode="out-in"
      >
        <Component
          :is="componentStrategies[step]"
          v-model="currentStepData"
          :code-errors="codeErrors"
          :email-errors="emailErrors"
          :password-errors="passwordErrors"
          :code-sent-at="codeSentAt"
          class="transition-opacity-300"
          @resend:code="console.log('UPDATE CODE'); resendCode();"
        />
      </Transition>

      <div class="flex flex-col gap-10 items-center">
        <BaseButton
          v-if="showOneButton"
          variant="primary"
          @click="goToNextStep"
        >
          {{ buttonLabel }}
        </BaseButton>

        <div
          v-if="!showOneButton" class="flex items-center gap-4 w-80"
        >
          <BaseButton
            variant="secondary"
            class="flex-1"
            @click="goToPreviousStep"
          >
            Back
          </BaseButton>

          <BaseButton
            variant="primary"
            class="flex-1"
            @click="goToNextStep"
          >
            {{ buttonLabel }}
          </BaseButton>
        </div>

        <BaseClickableText to="/login" text="Back to Log In" />
      </div>
    </form>
  </div>
</template>
